import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Form, Input, Select, Switch, Tabs } from "antd"
import { CloseOutlined, DeleteOutlined, MenuOutlined, PlusCircleFilled } from "@ant-design/icons"
import {
  DndContext,
  closestCenter,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import { Text } from "ui/Typography"
import { TemplateForm } from "pagesV2/pro/Settings/ProSettings/CustomTemplate/modal/AddNewTemplate/components/TemplateForm"
import { questionTypes } from "pagesV2/pro/Settings/ProSettings/CustomTemplate/Constant"
import { saveQuestionnaireTemplateQuestions } from "store/question"
import { toaster } from "components/common/Toast"
import styles from "./CreateQuestionTemplateModal.module.css"
import { spacings } from "themes/variables"

const generateId = () => Math.random().toString(36).substring(2, 11)

const makeOption = (label) => ({ optId: generateId(), label, value: "" })

const defaultQuestion = () => ({
  id: `q_${generateId()}`,
  question: "",
  type: "Paragraph",
  required: true,
  options: [makeOption("Option A"), makeOption("Option B")]
})

// Module-level helpers to avoid deep nesting inside setQuestions updaters
const applyOptionRemove = (q, optIdx) => ({ ...q, options: q.options.filter((_, i) => i !== optIdx) })
const applyOptionUpdate = (q, optIdx, value) => ({ ...q, options: q.options.map((opt, i) => (i === optIdx ? { ...opt, value } : opt)) })

const WITH_OPTIONS_TYPES = new Set(["Checkbox", "Radio", "Dropdown"])

const SortableRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <Flex
      ref={setNodeRef}
      align="center"
      gap="small"
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 }}
    >
      <button type="button" className={styles.dragHandle} {...listeners} {...attributes}>
        <MenuOutlined className={styles.dragIcon} />
      </button>
      {children}
    </Flex>
  )
}

SortableRow.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node
}

export const CreateQuestionTemplateModal = ({ open, onCancel, onSave }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.question)
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState("details")
  const [questions, setQuestions] = useState([defaultQuestion()])
  const [activeId, setActiveId] = useState(null)
  // { [questionId]: { question: bool, options: { [optIdx]: bool } } }
  const [questionErrors, setQuestionErrors] = useState({})

  // Separate Mouse and Touch sensors (not the unified PointerSensor): inside a
  // scrollable Drawer body, PointerSensor doesn't reliably stop the touch scroll
  // from stealing the gesture, while TouchSensor's delay does. Mouse drags on a
  // small move; touch needs a brief press-and-hold. Keyboard for a11y.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const activeQuestion = activeId ? questions.find((q) => q.id === activeId) : null

  const handleClose = useCallback(() => {
    form.resetFields()
    setQuestions([defaultQuestion()])
    setQuestionErrors({})
    setActiveTab("details")
    onCancel()
  }, [form, onCancel])

  const handleDragStart = ({ active }) => setActiveId(active.id)

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null)
    if (active && over && active.id !== over.id) {
      setQuestions((prev) => {
        const oldIndex = prev.findIndex((q) => q.id === active.id)
        const newIndex = prev.findIndex((q) => q.id === over.id)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  const addQuestion = () => setQuestions((prev) => [...prev, defaultQuestion()])

  const removeQuestion = (id) => {
    setQuestions((prev) => (prev.length > 1 ? prev.filter((q) => q.id !== id) : prev))
  }

  const updateQuestion = (id, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== id) return q
        // Checkbox starts with one option, Radio/Dropdown with two. Blank options are reset to that
        // count; typed options are kept and only topped up to the minimum.
        if (field === "type" && WITH_OPTIONS_TYPES.has(value)) {
          const minOptions = value === "Checkbox" ? 1 : 2
          const options = q.options || []
          const blankOptions = (from, count) =>
            Array.from({ length: count }, (_, i) => makeOption(`Option ${String.fromCodePoint(65 + from + i)}`))
          if (options.every((opt) => !opt.value.trim())) {
            return { ...q, [field]: value, options: blankOptions(0, minOptions) }
          }
          if (options.length < minOptions) {
            return { ...q, [field]: value, options: [...options, ...blankOptions(options.length, minOptions - options.length)] }
          }
        }
        return { ...q, [field]: value }
      })
    )
    if (field === "question") {
      setQuestionErrors((prev) => {
        if (!prev[id]?.question) return prev
        const next = { ...prev, [id]: { ...prev[id], question: false } }
        return next
      })
    }
  }

  const addOption = (questionId) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q
        const nextLabel = String.fromCodePoint(65 + q.options.length)
        return { ...q, options: [...q.options, makeOption(`Option ${nextLabel}`)] }
      })
    )
  }

  const removeOption = (questionId, optIdx) => {
    setQuestions((prev) => prev.map((q) => (q.id === questionId ? applyOptionRemove(q, optIdx) : q)))
  }

  const updateOption = (questionId, optIdx, value) => {
    setQuestions((prev) => prev.map((q) => (q.id === questionId ? applyOptionUpdate(q, optIdx, value) : q)))
    setQuestionErrors((prev) => {
      if (!prev[questionId]?.options?.[optIdx]) return prev
      const options = { ...prev[questionId].options, [optIdx]: false }
      return { ...prev, [questionId]: { ...prev[questionId], options } }
    })
  }

  const validateQuestions = () => {
    const errors = {}
    let isValid = true
    questions.forEach((q) => {
      const qError = { question: false, options: {} }
      if (!q.question.trim()) {
        qError.question = true
        isValid = false
      }
      if (WITH_OPTIONS_TYPES.has(q.type)) {
        q.options.forEach((opt, optIdx) => {
          if (!opt.value.trim()) {
            qError.options[optIdx] = true
            isValid = false
          }
        })
      }
      if (qError.question || Object.keys(qError.options).length > 0) {
        errors[q.id] = qError
      }
    })
    setQuestionErrors(errors)
    return isValid
  }

  const handleTabChange = async (tab) => {
    if (tab === "questions" && activeTab === "details") {
      try {
        await form.validateFields()
        setActiveTab(tab)
      } catch {
        // form validation errors are shown inline by antd
      }
      return
    }
    setActiveTab(tab)
  }

  const handleNext = () => handleTabChange("questions")

  const handleSave = async () => {
    if (!validateQuestions()) return
    try {
      const values = form.getFieldsValue()
      const payload = {
        questionnaire_template: {
          name: values.name,
          description: values.description,
          trip_ids: [],
          questions_attributes: questions.map((q) => ({
            question_text: q.question,
            question_type: q.type.toLowerCase(),
            required: q.required,
            questions_options_attributes: q.type === "Paragraph" ? [] : q.options.map((opt) => ({ option_text: opt.value }))
          }))
        }
      }
      const res = await dispatch(saveQuestionnaireTemplateQuestions(payload))
      toaster.success(res.message)
      handleClose()
      onSave?.(res)
    } catch {
      toaster.error("Failed to save template")
    }
  }

  // The card body for a question. Shared by the sortable list and the DragOverlay so
  // the floating item while dragging matches the settled rows exactly.
  const renderQuestionRow = (q) => (
    <div className={styles.questionRow}>
      <Flex align="center" gap={spacings.XS} wrap className={styles.controlsRow}>
        <Form.Item
          className={styles.questionField}
          validateStatus={questionErrors[q.id]?.question ? "error" : ""}
          help={questionErrors[q.id]?.question ? "Question text is required" : undefined}
        >
          <Input
            placeholder="Enter question"
            value={q.question}
            onChange={(e) => updateQuestion(q.id, "question", e.target.value)}
          />
        </Form.Item>
        <Select
          value={q.type}
          options={questionTypes}
          onChange={(val) => updateQuestion(q.id, "type", val)}
          className={styles.typeSelect}
        />
        <Flex align="center" gap={4} className={styles.requiredField}>
          <Switch
            checked={q.required}
            size="small"
            onChange={(checked) => updateQuestion(q.id, "required", checked)}
          />
          <Text className={styles.requiredLabel}>{"Required"}</Text>
        </Flex>
        <Button
          type="text"
          icon={<DeleteOutlined className={styles.deleteIcon} />}
          onClick={() => removeQuestion(q.id)}
          disabled={questions.length === 1}
          className={styles.deleteBtn}
        />
      </Flex>

      {WITH_OPTIONS_TYPES.has(q.type) && (
        <Flex vertical gap="small" className={styles.optionsList}>
          {q.options.map((opt, optIdx) => (
            <Flex key={opt.optId} align="center" gap={spacings.XS} className={styles.optionRow}>
              <Form.Item
                className={styles.optionField}
                validateStatus={questionErrors[q.id]?.options?.[optIdx] ? "error" : ""}
                help={questionErrors[q.id]?.options?.[optIdx] ? "Option text is required" : undefined}
              >
                <Input
                  placeholder="Enter choice"
                  value={opt.value}
                  onChange={(e) => updateOption(q.id, optIdx, e.target.value)}
                  className={styles.optionInput}
                />
              </Form.Item>
              {/* Checkbox questions may have a single option; Radio/Dropdown need at least two */}
              {q.options.length > (q.type === "Checkbox" ? 1 : 2) && (
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => removeOption(q.id, optIdx)}
                  className={styles.removeOptionBtn}
                />
              )}
            </Flex>
          ))}
          <Button type="link" icon={<PlusCircleFilled />} onClick={() => addOption(q.id)} className={styles.addChoiceBtn}>
            {"Add Choice"}
          </Button>
        </Flex>
      )}
    </div>
  )

  const tabItems = [
    {
      key: "details",
      label: "Details",
      children: (
        <Form form={form} layout="vertical">
          <TemplateForm />
        </Form>
      )
    },
    {
      key: "questions",
      label: "Questions",
      children: (
        <Flex vertical gap="small">
          <Text strong className={styles.questionsTitle}>{"Questions"}</Text>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
              {questions.map((q) => (
                <SortableRow key={q.id} id={q.id}>
                  {renderQuestionRow(q)}
                </SortableRow>
              ))}
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                <Flex align="flex-start" gap="small" className={styles.dragOverlay}>
                  <button type="button" className={styles.dragHandle}>
                    <MenuOutlined className={styles.dragIcon} />
                  </button>
                  {/* Static preview only — the overlay is portaled outside the tree,
                      so it must not render Form.Item / interactive controls. */}
                  <div className={styles.questionRow}>
                    <Flex align="center" gap={spacings.XS} wrap className={styles.controlsRow}>
                      <Input readOnly value={activeQuestion?.question} placeholder="Enter question" className={styles.questionField} />
                      <Select value={activeQuestion?.type} options={questionTypes} open={false} className={styles.typeSelect} />
                      <Flex align="center" gap={4} className={styles.requiredField}>
                        <Switch checked={activeQuestion?.required} size="small" />
                        <Text className={styles.requiredLabel}>{"Required"}</Text>
                      </Flex>
                      <Button type="text" icon={<DeleteOutlined className={styles.deleteIcon} />} className={styles.deleteBtn} />
                    </Flex>
                  </div>
                </Flex>
              ) : null}
            </DragOverlay>
          </DndContext>

          <button type="button" className={styles.addQuestionBtn} onClick={addQuestion}>
            <PlusCircleFilled className={styles.addQuestionIcon} />
            <Text className={styles.addQuestionText}>{"Add Question"}</Text>
          </button>
        </Flex>
      )
    }
  ]

  const content = (
    <>
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} className={styles.tabs} />
      <Flex gap={spacings.XS} className={styles.footer}>
        {activeTab === "details" ? (
          <>
            <Button color="primary" variant="outlined" onClick={handleClose}>
              {t("global.cancel") || "Cancel"}
            </Button>
            <Button color="primary" variant="solid" onClick={handleNext}>
              {t("global.next") || "Next"}
            </Button>
          </>
        ) : (
          <>
            <Button color="primary" variant="outlined" onClick={() => setActiveTab("details")}>
              {t("global.back") || "Back"}
            </Button>
            <Button color="primary" variant="solid" loading={loading} disabled={loading} onClick={handleSave}>
              {t("settings.save") || "Save"}
            </Button>
          </>
        )}
      </Flex>
    </>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => handleClose()} title={"Create Question Template"}>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      title={"Create Question Template"}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={640}
      maskClosable={false}
      destroyOnClose
    >
      {content}
    </Modal>
  )
}

CreateQuestionTemplateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func
}
